/**
 * useShellArmedWeapon - the weapon in the actor's hand, as everything else reads it.
 *
 * - `slot`: the armed plate (null with none); `isMelee` its kind.
 * - `effectiveSlot`: the same plate with the per-weapon overrides applied -
 *   the SAME reference as `slot` when no override is active. It drives the
 *   probability plots and the Effective Profile card; the raw `slot` keeps
 *   driving editors and persistence.
 * - `actorAmmo`: what is in that magazine, for the ring around the actor's
 *   marker - the magazine `projectLoadout` overlays, not a second copy, and
 *   ONLY THE ACTOR HAS ONE (the selection belongs to this panel).
 * - `attackerCharStats`: resolved for the SELECTED weapon (A3), with the
 *   talent flags read off it.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useMemo } from 'react';
import { applyWeaponOverrides } from '../engine/weaponOverrides';
import { projectAttacker } from '../engine/roster/projection';

/** @param {object} args - actor, selectedSlot, currentSlots, weaponOverrides. */
export default function useShellArmedWeapon({ actor, selectedSlot, currentSlots, weaponOverrides }) {
  const actorAmmo = useMemo(
    () => (selectedSlot === null ? null : (currentSlots[selectedSlot]?.ammo ?? null)),
    [currentSlots, selectedSlot],
  );
  const attackerCharStats = useMemo(() => (actor ? projectAttacker(actor, selectedSlot) : null), [actor, selectedSlot]);
  const slot = selectedSlot !== null ? currentSlots[selectedSlot] : null;
  const effectiveSlot = useMemo(
    () => (slot ? applyWeaponOverrides(slot, weaponOverrides) : null),
    [slot, weaponOverrides],
  );
  return {
    slot,
    isMelee: slot?.weaponType === 'melee',
    effectiveSlot,
    actorAmmo,
    attackerCharStats,
    attackerSourceCharacter: actor?.sheet ?? null,
    hasHammerBlow: attackerCharStats?.hasHammerBlow ?? false,
  };
}
